import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DAY_STATUSES, type DayStatus } from "@sundial/core";
import { useLogDay, useClearDay, useClients, useSettings } from "../api/queries.js";
import type { DayAllocation } from "../api/queries.js";

const weightOf = (s: "full" | "half"): number => (s === "full" ? 1 : 0.5);

/** One client allocation: avatar, client picker, Full/Half toggle, remove/Default. */
function AllocRow({
  t,
  clients,
  clientId,
  onClient,
  status,
  onStatus,
  onRemove,
  removeLabel,
  isDefault,
}: {
  t: (k: string, o?: Record<string, unknown>) => string;
  clients: { id: string; name: string }[];
  clientId: string;
  onClient: (id: string) => void;
  status: "full" | "half";
  onStatus: (s: "full" | "half") => void;
  onRemove?: () => void;
  removeLabel?: string;
  isDefault?: boolean;
}) {
  const name = clients.find((c) => c.id === clientId)?.name ?? "";
  const avatar = isDefault ? "★" : (name.trim()[0] ?? "?").toUpperCase();
  const toggle = (active: boolean) => ({
    flex: 1,
    height: 44,
    borderRadius: 9,
    cursor: "pointer",
    font: "600 13px var(--font-head)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: ".14s",
    border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
    background: active ? "var(--primary)" : "var(--card)",
    color: active ? "#fff" : "var(--muted)",
    boxShadow: active ? "0 2px 8px var(--glow)" : "none",
  });
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 13, background: "var(--surface)", padding: "11px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 32,
            height: 32,
            flex: "none",
            borderRadius: 9,
            background: isDefault ? "color-mix(in srgb, var(--primary) 14%, transparent)" : "var(--card)",
            color: isDefault ? "var(--primary-deep, var(--primary))" : "var(--text)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: "700 13px var(--font-head)",
          }}
        >
          {avatar}
        </span>
        <select
          aria-label={t("dayEditor.client")}
          value={clientId}
          onChange={(e) => onClient(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            height: 40,
            borderRadius: 9,
            border: "1px solid var(--border)",
            background: "var(--card)",
            color: "var(--text)",
            padding: "0 10px",
            font: "600 13.5px var(--font-head)",
            cursor: "pointer",
            outline: "none",
          }}
        >
          <option value="">—</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={removeLabel}
            style={{ width: 40, height: 40, flex: "none", border: "1px solid var(--border)", borderRadius: 9, background: "var(--card)", color: "var(--muted2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
        {isDefault && (
          <span style={{ font: "700 9px var(--font-head)", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--muted2)", background: "var(--card)", border: "1px solid var(--border)", padding: "6px 8px", borderRadius: 7, whiteSpace: "nowrap" }}>
            {t("dayEditor.default")}
          </span>
        )}
      </div>
      <div role="group" aria-label={t("dayEditor.amount")} style={{ display: "flex", gap: 7, marginTop: 9 }}>
        <button type="button" aria-pressed={status === "full"} onClick={() => onStatus("full")} style={toggle(status === "full")}>
          {t("status.full")}
        </button>
        <button type="button" aria-pressed={status === "half"} onClick={() => onStatus("half")} style={toggle(status === "half")}>
          {t("status.half")}
        </button>
      </div>
    </div>
  );
}

/* ── SVG icons per status ────────────────────────────────── */
const STATUS_ICON: Record<DayStatus, JSX.Element> = {
  full: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 4v1M12 19v1M5 5l1 1M18 18l1 1M4 12h1M19 12h1M5 19l1-1M18 6l1-1" />
    </svg>
  ),
  half: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 17h16M8 17a4 4 0 0 1 8 0M12 5v2M6.5 8.8l1.2 1.1M17.5 8.8l-1.2 1.1" />
    </svg>
  ),
  none: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
    </svg>
  ),
  sick: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M12 9v6M9 12h6" />
    </svg>
  ),
  holiday: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 21V4M6 4h11l-2 3 2 3H6" />
    </svg>
  ),
};

export interface DayEditorProps {
  date: string;
  current: DayStatus | null;
  month: string;
  currentNote?: string;
  currentClientId?: string | null;
  currentAllocations?: DayAllocation[];
  onClose: (savedDate?: string, savedStatus?: DayStatus, prevStatus?: DayStatus | null) => void;
}

export function DayEditor({ date, current, month, currentNote, currentClientId, currentAllocations, onClose }: DayEditorProps) {
  const { t } = useTranslation();
  const logDay = useLogDay(month);
  const clearDay = useClearDay(month);
  const { data: clientsData } = useClients();
  const { data: settingsData } = useSettings();
  const isDesktop =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(min-width: 1024px)").matches;
  const clients = (clientsData?.clients ?? []).filter((c) => !c.archived);
  const hasClients = clients.length > 1; // more than the default
  // The app default (the client new days bill to), resolved live from settings.
  const defaultClientId = settingsData?.defaultClientId ?? clients[0]?.id ?? "";
  // A new day bills to the DEFAULT client — the real business the user set as
  // default — not whichever client happens to be first in the list.
  const preferredPrimary = defaultClientId;

  // The editor configures locally, then commits on Save (per the design).
  const [kind, setKind] = useState<DayStatus>(current ?? "full");
  const [primaryClientId, setPrimaryClientId] = useState(currentClientId ?? preferredPrimary);
  const [extras, setExtras] = useState<DayAllocation[]>(currentAllocations ?? []);
  const [note, setNote] = useState(currentNote ?? "");

  useEffect(() => {
    setKind(current ?? "full");
    setPrimaryClientId(currentClientId ?? preferredPrimary);
    setExtras(currentAllocations ?? []);
    setNote(currentNote ?? "");
  }, [date]);

  const billable = kind === "full" || kind === "half";
  const totalDays = (billable ? weightOf(kind) : 0) + extras.reduce((s, a) => s + weightOf(a.status), 0);
  const clientCount = billable ? 1 + extras.length : 0;
  const clientName = (id: string) => clients.find((c) => c.id === id)?.name ?? "";

  function handleSave() {
    logDay.mutate({
      date,
      status: kind,
      note: note.trim() || undefined,
      // Only send the primary clientId when it actually changed, so a status-only
      // edit never re-snapshots (re-prices) the primary's frozen rate.
      ...(primaryClientId !== (currentClientId ?? defaultClientId) ? { clientId: primaryClientId } : {}),
      allocations: billable ? extras.filter((e) => e.clientId) : [],
    });
    onClose(date, kind, current ?? null);
  }

  function handleClear() {
    clearDay.mutate(date);
    onClose();
  }
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  /* ── Focus trap ──────────────────────────────────────────── */
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    // Focus first focusable element
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable[0]?.focus();

    function trapFocus(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const all = el!.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (all.length === 0) return;
      const first = all[0]!;
      const last = all[all.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }

    document.addEventListener("keydown", trapFocus);
    return () => document.removeEventListener("keydown", trapFocus);
  }, [onClose]);

  /* ── Formatted date label ────────────────────────────────── */
  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(date + "T12:00:00"));

  return (
    /* Backdrop */
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(15,26,48,.42)",
        display: "flex",
        // Desktop: a centered modal. Mobile: a bottom sheet.
        alignItems: isDesktop ? "center" : "flex-end",
        justifyContent: "center",
        padding: isDesktop ? "24px" : 0,
        animation: "scRise .15s ease both",
      }}
    >
      {/* Dialog card */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("dayEditor.title", { date: dateLabel })}
        style={{
          width: "100%",
          maxWidth: isDesktop ? "560px" : "520px",
          maxHeight: isDesktop ? "88vh" : undefined,
          overflowY: isDesktop ? "auto" : undefined,
          margin: "0 auto",
          background: "var(--card)",
          borderRadius: isDesktop ? "20px" : "22px 22px 0 0",
          padding: isDesktop ? "18px 24px 24px" : "16px 18px 22px",
          boxShadow: isDesktop ? "0 24px 60px rgba(15,26,48,.34)" : "0 -10px 40px rgba(15,26,48,.28)",
          animation: isDesktop ? "scRise .18s ease both" : "scSheet .22s ease both",
        }}
      >
        {/* Drag handle — mobile bottom-sheet only */}
        {!isDesktop && (
          <div style={{ width: 38, height: 4, borderRadius: 2, background: "var(--border)", margin: "0 auto 15px" }} />
        )}

        {/* Header row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "10px",
            marginBottom: "4px",
          }}
        >
          <div>
            <div style={{ font: "700 18px/1.2 var(--font-head)", letterSpacing: "-.01em" }}>
              {dateLabel}
            </div>
            <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "4px" }}>
              {t(`status.${kind}`)}
            </div>
          </div>
          <button
            onClick={() => onClose()}
            aria-label={t("close")}
            style={{
              width: "34px",
              height: "34px",
              flexShrink: 0,
              border: "none",
              borderRadius: "9px",
              background: "var(--surface)",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Day-kind row — active is orange-filled, per the design's statusStyle. */}
        <div role="group" aria-label={t("dayEditor.title", { date: dateLabel })} style={{ display: "flex", gap: "6px", margin: "16px 0 16px" }}>
          {DAY_STATUSES.map((status) => {
            const isActive = status === kind;
            return (
              <button
                key={status}
                aria-pressed={isActive}
                onClick={() => setKind(status)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 56,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  borderRadius: 12,
                  cursor: "pointer",
                  font: "600 10px var(--font-head)",
                  padding: "7px 3px",
                  textAlign: "center",
                  transition: "all .15s",
                  border: isActive ? "1px solid var(--primary)" : "1px solid var(--border)",
                  background: isActive ? "var(--primary)" : "var(--surface)",
                  color: isActive ? "#fff" : "var(--muted)",
                  boxShadow: isActive ? "0 2px 10px var(--glow)" : "none",
                }}
              >
                {STATUS_ICON[status]}
                {t(`status.${status}`)}
              </button>
            );
          })}
        </div>

        {/* Billed to — one row per client, each full or half. Only for billable days. */}
        {billable && hasClients && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
              <span style={{ font: "600 11px/14px var(--font-head)", letterSpacing: ".07em", textTransform: "uppercase", color: "var(--muted2)" }}>
                {t("dayEditor.billedTo")}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 999, padding: "5px 11px" }}>
                <span style={{ font: "700 12px 'JetBrains Mono', monospace", color: "var(--text)" }}>
                  {totalDays.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}
                </span>
                <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
                  {t("dayEditor.daysClients", { count: clientCount })}
                </span>
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {/* Primary row — its Full/Half is the day kind; the client is the primary. */}
              <AllocRow
                t={t}
                clients={clients}
                clientId={primaryClientId}
                onClient={setPrimaryClientId}
                status={kind === "half" ? "half" : "full"}
                onStatus={(s) => setKind(s)}
                isDefault={primaryClientId === defaultClientId}
              />
              {extras.map((a, i) => (
                <AllocRow
                  key={i}
                  t={t}
                  clients={clients}
                  clientId={a.clientId}
                  onClient={(id) => setExtras(extras.map((x, idx) => (idx === i ? { ...x, clientId: id } : x)))}
                  status={a.status}
                  onStatus={(s) => setExtras(extras.map((x, idx) => (idx === i ? { ...x, status: s } : x)))}
                  onRemove={() => setExtras(extras.filter((_, idx) => idx !== i))}
                  removeLabel={t("dayEditor.removeClient", { name: clientName(a.clientId) })}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => setExtras([...extras, { clientId: "", status: "full" }])}
              style={{
                width: "100%",
                height: 44,
                marginTop: 11,
                border: "1px dashed color-mix(in srgb, var(--primary) 42%, var(--border))",
                borderRadius: 11,
                background: "none",
                color: "var(--primary-deep, var(--primary))",
                font: "600 13.5px var(--font-head)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("dayEditor.addClient")}
            </button>
          </div>
        )}

        {!billable && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 12, background: "var(--surface)", padding: "13px 14px", marginBottom: 2 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted2)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.4 }}>{t("dayEditor.notBilledNote")}</span>
          </div>
        )}

        {/* Note textarea */}
        <label
          style={{
            font: "600 11px/14px var(--font-head)",
            letterSpacing: ".07em",
            textTransform: "uppercase",
            color: "var(--muted2)",
            display: "block",
            marginBottom: "7px",
          }}
        >
          {t("dayEditor.note")}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("dayEditor.notePlaceholder")}
          style={{
            width: "100%",
            minHeight: "62px",
            resize: "none",
            borderRadius: "10px",
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            padding: "11px 13px",
            fontSize: "14px",
            fontFamily: "Inter, system-ui, sans-serif",
            outline: "none",
          }}
        />

        {/* Save — full width, per the design (close ✕ in the header cancels). */}
        <button
          onClick={handleSave}
          style={{
            width: "100%",
            height: "46px",
            marginTop: "14px",
            border: "none",
            borderRadius: "11px",
            background: "var(--primary)",
            color: "#fff",
            font: "600 15px var(--font-head)",
            cursor: "pointer",
            boxShadow: "0 3px 12px var(--glow)",
          }}
        >
          {t("common.save")}
        </button>
        {/* Clear a logged day — a subtle text action, not in the design's happy path. */}
        {current && (
          <button
            onClick={handleClear}
            style={{
              width: "100%",
              height: "36px",
              marginTop: "8px",
              border: "none",
              background: "none",
              color: "var(--danger)",
              font: "600 12.5px var(--font-head)",
              cursor: "pointer",
            }}
          >
            {t("clear")}
          </button>
        )}
      </div>
    </div>
  );
}
